import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { KG_PER_LB, formatUnit, seatRows, unitLabel, type BaggageArea, type LoadDataInput, type LoadWarning } from '@jupi/shared'
import { useUnitSystem } from '@jupi/ui'

// A top-down wireframe of the aircraft the pilot is loading: the seat rows
// and baggage compartments it actually has (a Cessna 152 is one row of two
// seats and two baggage areas), each with its own entry box, so the load is
// typed where the weight sits. Seat rows are entered as the row's total, the
// way the loading sheet takes them. A box turns red when the load exceeds
// what the compartment holds.

export interface LoadingLayoutAircraft {
  hasRearSeats?: boolean
  baggageAreas?: BaggageArea[]
  baggageMaxTotalKg?: number | null
}

type Change = Partial<LoadDataInput>

// The fuselage's fill. Opaque, so a field's border can be the same colour
// and only its inside shows white.
const fuselageFill = (mode: 'light' | 'dark') => (mode === 'dark' ? '#2a2f38' : '#eef1f5')

// One mass in kg, shown and typed in the member's unit system (kg or lb). The
// name is a caption above the field, so the field itself is just a white box.
function MassBox({
  label,
  kg,
  maxKg,
  over,
  fill,
  onChange,
}: {
  label: string
  kg: number
  maxKg?: number
  over: boolean
  fill: string
  onChange: (kg: number) => void
}) {
  const system = useUnitSystem()
  const factor = system === 'imperial' ? 1 / KG_PER_LB : 1
  const unit = system === 'imperial' ? 'lb' : 'kg'
  const shown = Math.round(kg * factor * 10) / 10
  const [text, setText] = useState<string | null>(null)
  return (
    <Box sx={{ width: '100%', maxWidth: 104, textAlign: 'center' }}>
      <Typography variant="caption" color={over ? 'error' : 'text.secondary'} sx={{ display: 'block', lineHeight: 1.3, mb: 0.25, whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
        {label}
        {maxKg ? ` · max ${Math.round(maxKg * factor * 10) / 10} ${unit}` : ''}
      </Typography>
      <TextField
        size="small"
        error={over}
        value={text ?? (shown ? String(shown) : '')}
        placeholder="0"
        onFocus={(e) => e.target.select()}
        onBlur={() => setText(null)}
        onChange={(e) => {
          setText(e.target.value)
          const n = Number(e.target.value.replace(',', '.'))
          if (e.target.value.trim() === '') onChange(0)
          else if (Number.isFinite(n) && n >= 0 && n <= 5000) onChange(n / factor)
        }}
        slotProps={{
          htmlInput: { inputMode: 'decimal', enterKeyHint: 'next', 'aria-label': label, style: { fontSize: '1.15rem', textAlign: 'center' } },
          input: { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> },
        }}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': { bgcolor: '#fff', color: '#1b1f27', '& fieldset': { borderColor: over ? undefined : fill, borderWidth: 2 } },
          '& .MuiInputAdornment-root': { color: '#5b6472' },
        }}
      />
    </Box>
  )
}

// A seat seen from above, nose up: head rest, back rest behind the cushion.
function Seat() {
  const theme = useTheme()
  const body = theme.palette.mode === 'dark' ? '#4a5260' : '#c9d0da'
  const edge = theme.palette.mode === 'dark' ? '#7b8494' : '#8d97a6'
  return (
    <svg width="34" height="41" viewBox="0 0 44 52" aria-hidden>
      <rect x="9" y="30" width="26" height="9" rx="4.5" fill={body} stroke={edge} strokeWidth="1.5" />
      <rect x="3" y="3" width="6" height="30" rx="3" fill={body} stroke={edge} strokeWidth="1.5" />
      <rect x="35" y="3" width="6" height="30" rx="3" fill={body} stroke={edge} strokeWidth="1.5" />
      <rect x="9" y="3" width="26" height="29" rx="8" fill={body} stroke={edge} strokeWidth="1.5" />
      <rect x="14" y="39" width="16" height="9" rx="4" fill={body} stroke={edge} strokeWidth="1.5" />
    </svg>
  )
}

export default function LoadingLayout({
  aircraft,
  load,
  warnings,
  onChange,
}: {
  aircraft: LoadingLayoutAircraft
  load: LoadDataInput
  warnings: LoadWarning[]
  onChange: (change: Change) => void
}) {
  const { t } = useTranslation()
  const system = useUnitSystem()
  const theme = useTheme()
  const rows = seatRows(aircraft)
  const areas = aircraft.baggageAreas ?? []
  const stroke = theme.palette.text.secondary
  const fill = fuselageFill(theme.palette.mode)
  const overArea = (index: number) => warnings.some((w) => w.code === 'baggageArea' && w.index === index)
  const overTotal = warnings.some((w) => w.code === 'baggageTotal')

  const setBaggage = (index: number, kg: number) => {
    const baggageKg = areas.map((_, i) => (i === index ? kg : (load.baggageKg?.[i] ?? 0)))
    onChange({ baggageKg, cargoKg: baggageKg.reduce((sum, v) => sum + v, 0) })
  }

  const seatRow = (label: string, key: 'frontKg' | 'rearKg') => (
    <Box key={key} sx={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 104px) 34px', justifyContent: 'center', alignItems: 'end', gap: 0.5 }}>
      <Seat />
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <MassBox label={label} kg={load[key]} over={false} fill={fill} onChange={(kg) => onChange({ [key]: kg })} />
      </Box>
      <Seat />
    </Box>
  )

  return (
    <Box role="group" aria-label={t('plan.weight.layout')} sx={{ position: 'relative', mx: 'auto', maxWidth: 340 }}>
      {/* The fuselage from above, nose up: a cowling, a cabin that is widest at the
          seats, and a tail that narrows. Stretches with the rows it holds. */}
      <svg
        aria-hidden
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        <path
          d="M 12 3 C 30 0.5 70 0.5 88 3 C 92 3.5 93 6 93 10 L 93 50 C 93 64 85 84 76 99.5 L 24 99.5 C 15 84 7 64 7 50 L 7 10 C 7 6 8 3.5 12 3 Z"
          fill={fill}
          stroke={stroke}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
        <path d="M 13 9 C 35 6.5 65 6.5 87 9" fill="none" stroke={stroke} strokeWidth="1.5" vectorEffect="non-scaling-stroke" opacity="0.6" />
      </svg>
      <Box sx={{ position: 'relative', px: '13%', pt: '13%', pb: '16%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {seatRow(t(rows === 1 ? 'plan.weight.seats' : 'plan.weight.frontRow'), 'frontKg')}
        {rows === 2 ? seatRow(t('plan.weight.rearRow'), 'rearKg') : null}
        <Box sx={{ borderTop: '1px dashed', borderColor: overTotal ? 'error.main' : 'divider', pt: 1.5 }}>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon icon="mdi:bag-suitcase-outline" width={26} style={{ position: 'absolute', left: 0 }} color={overTotal ? theme.palette.error.main : theme.palette.text.secondary} aria-label={t('plan.weight.baggage')} />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, width: 104 }}>
              {areas.length === 0 ? (
                <MassBox label={t('performance.armCargo')} kg={load.cargoKg} over={false} fill={fill} onChange={(kg) => onChange({ cargoKg: kg })} />
              ) : (
                areas.map((area, index) => (
                  <MassBox
                    key={`${area.label}-${index}`}
                    label={t('plan.weight.baggageArea', { label: area.label })}
                    kg={load.baggageKg?.[index] ?? 0}
                    maxKg={area.maxKg}
                    over={overArea(index) || overTotal}
                    fill={fill}
                    onChange={(kg) => setBaggage(index, kg)}
                  />
                ))
              )}
            </Box>
          </Box>
        </Box>
        {aircraft.baggageMaxTotalKg && areas.length > 1 ? (
          <Typography
            variant="caption"
            color={overTotal ? 'error' : 'text.secondary'}
            sx={{ textAlign: 'center', lineHeight: 1.2, mt: -0.5 }}
          >
            {t('plan.weight.baggageTogether', { max: `${formatUnit('mass', aircraft.baggageMaxTotalKg, system)} ${unitLabel('mass', system)}` })}
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}
